import { getDepots, deleteDepots, updateDepotStatus, updateDepots } from '@spark/services/depots';
import { notification } from '@spark/ui-components/utils/notification';
import { useLatestAsyncRequest } from '@spark/ui-components/hooks/useLatestAsyncRequest';
import { useState, useEffect, useCallback, useRef } from 'react';

const initialFilters = {
  Search: '',
  ReferenciaCLiente: '',
  TipoDepot: '',
  NumeroContainer: '',
  TipoContainer: '',
  NumeroEmbarcador: '',
  StatusPedido: '',
  StatusPedidoEmissaoTMS: '',
  StatusDepot: '',
  IdMotoristaColeta: '',
  IdVeiculoColeta: '',
  IdMotoristaEntrega: '',
  IdVeiculoEntrega: '',
  SituacaoDepot: '',
  StatusCTNR: '',
  DataColeta: '',
  DataColetaInicio: '',
  DataColetaFim: '',
  DataEntrega: '',
  DataEntregaInicio: '',
  DataEntregaFim: '',
  DataDevolucao: '',
  DataDevolucaoInicio: '',
  DataDevolucaoFim: '',
  FreeTime: '',
  Status: '',
};

const toApiDateTime = (value) => {
  if (!value) return undefined;
  if (typeof value !== 'string') return value;
  if (value.includes('T')) {
    return value.includes(':') && value.length >= 19 ? value : `${value}T00:00:00`;
  }
  if (value.includes(' ')) {
    const [datePart, timePart] = value.split(' ');
    const normalizedTime = timePart?.length === 5 ? `${timePart}:00` : timePart;
    return `${datePart}T${normalizedTime || '00:00:00'}`;
  }
  return `${value}T00:00:00`;
};

const toSelectValue = (value) => {
  if (value && typeof value === 'object' && 'value' in value) {
    return value.value;
  }
  return value;
};

export const useDepotsListagem = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [resumoStatusPendentes, setResumoStatusPendentes] = useState({});
  const [paginationState, setPaginationState] = useState({pageIndex: 0, pageSize: 5});
  const { beginRequest, isCurrentRequest, isMounted } = useLatestAsyncRequest();
  const didInitialLoadRef = useRef(false);
  const inFlightSignatureRef = useRef(null);
  const [filtros, setFiltros] = useState(initialFilters);

  const toBooleanStatus = (value) =>
    value === true || value === 1 || value === '1' || value === 'true';

  const fetchData = useCallback(async (filtrosParaBuscar = filtros) => {
    if (!isMounted()) return;

    let requestId = null;
    try {
      const payload = {
        limit: paginationState.pageSize,
        page: paginationState.pageIndex + 1,
        Search: filtrosParaBuscar.Search || undefined,
        ReferenciaCLiente: filtrosParaBuscar.ReferenciaCLiente || undefined,
        TipoDepot: filtrosParaBuscar.TipoDepot || undefined,
        NumeroContainer: filtrosParaBuscar.NumeroContainer || undefined,
        TipoContainer: filtrosParaBuscar.TipoContainer || undefined,
        NumeroEmbarcador: filtrosParaBuscar.NumeroEmbarcador || undefined,
        StatusPedido: filtrosParaBuscar.StatusPedido || undefined,
        StatusPedidoEmissaoTMS: filtrosParaBuscar.StatusPedidoEmissaoTMS || undefined,
        StatusDepot: filtrosParaBuscar.StatusDepot || undefined,
        Status: filtrosParaBuscar.Status || undefined,
        IdMotoristaColeta: toSelectValue(filtrosParaBuscar.IdMotoristaColeta) || undefined,
        IdVeiculoColeta: toSelectValue(filtrosParaBuscar.IdVeiculoColeta) || undefined,
        IdMotoristaEntrega: toSelectValue(filtrosParaBuscar.IdMotoristaEntrega) || undefined,
        IdVeiculoEntrega: toSelectValue(filtrosParaBuscar.IdVeiculoEntrega) || undefined,
        SituacaoDepot: filtrosParaBuscar.SituacaoDepot || undefined,
        StatusCTNR: filtrosParaBuscar.StatusCTNR || undefined,
        DataColeta: toApiDateTime(filtrosParaBuscar.DataColeta),
        DataColetaInicio: toApiDateTime(filtrosParaBuscar.DataColetaInicio),
        DataColetaFim: toApiDateTime(filtrosParaBuscar.DataColetaFim),
        DataEntrega: toApiDateTime(filtrosParaBuscar.DataEntrega),
        DataEntregaInicio: toApiDateTime(filtrosParaBuscar.DataEntregaInicio),
        DataEntregaFim: toApiDateTime(filtrosParaBuscar.DataEntregaFim),
        DataDevolucao: toApiDateTime(filtrosParaBuscar.DataDevolucao),
        DataDevolucaoInicio: toApiDateTime(filtrosParaBuscar.DataDevolucaoInicio),
        DataDevolucaoFim: toApiDateTime(filtrosParaBuscar.DataDevolucaoFim),
        LocalDevolucao: filtrosParaBuscar.LocalDevolucao || undefined,
        FreeTime: toApiDateTime(filtrosParaBuscar.FreeTime),
      };

      const signature = JSON.stringify(payload);
      if (inFlightSignatureRef.current === signature) return;

      inFlightSignatureRef.current = signature;
      requestId = beginRequest();
      setLoading(true);
      const responseData = await getDepots(payload);
      if (!isCurrentRequest(requestId)) return;

      const dadosProcessados = (responseData.registros || []).map((item) => ({
        id: item.Id,
        referenciaCliente: item.ReferenciaCLiente || item.ReferenciaCliente || '',
        tipoDepot: item.TipoDepot || '',
        numeroContainer: item.NumeroContainer || '',
        tipoContainer: item.TipoContainer || '',
        numeroEmbarcador: item.NumeroEmbarcador || '',
        localColeta: item.LocalColeta || '',
        dataColeta: item.DataColeta
          ? new Date(item.DataColeta).toLocaleString('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '',
        destino: item.Local || '',
        dataEntrega: item.DataEntrega
          ? new Date(item.DataEntrega).toLocaleString('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '',
        localDevolucao: item.LocalDevolucao || '',
        dataDevolucao: item.DataDevolucao
          ? new Date(item.DataDevolucao).toLocaleString('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '',
        freeTime: item.FreeTime
          ? new Date(item.FreeTime).toLocaleString('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '',
        statusPedido: item.StatusPedido || '',
        statusPedidoEmissaoTMS: item.StatusPedidoEmissaoTMS || '',
        status: item.Status || '',
        statusDepot: item.StatusDepot || '',
        situacaoDepot: item.SituacaoDepot ?? '',
        statusCTNR: item.StatusCTNR ?? '',
        idMotoristaColeta: item.IdMotoristaColeta ?? null,
        motoristaColeta: item.MotoristaColeta?.Nome || '',
        idMotoristaEntrega: item.IdMotoristaEntrega ?? null,
        motoristaEntrega: item.MotoristaEntrega?.Nome || '',
        idMotoristaDevolucao: item.IdMotoristaDevolucao ?? null,
        motoristaDevolucao: item.MotoristaDevolucao?.Nome || '',
        idVeiculoColeta: item.IdVeiculoColeta ?? null,
        veiculoColeta: item.VeiculoColeta
          ? `${item.VeiculoColeta.Placa || ''}${item.VeiculoColeta.Frota ? ` - ${item.VeiculoColeta.Frota}` : ''}`
          : '',
        idVeiculoEntrega: item.IdVeiculoEntrega ?? null,
        veiculoEntrega: item.VeiculoEntrega
          ? `${item.VeiculoEntrega.Placa || ''}${item.VeiculoEntrega.Frota ? ` - ${item.VeiculoEntrega.Frota}` : ''}`
          : '',
        idVeiculoDevolucao: item.IdVeiculoDevolucao ?? null,
        veiculoDevolucao: item.VeiculoDevolucao
          ? `${item.VeiculoDevolucao.Placa || ''}${item.VeiculoDevolucao.Frota ? ` - ${item.VeiculoDevolucao.Frota}` : ''}`
          : '',
        motDepot: item.MotDepot || '',
        anotacoesProgramacao: item.AnotacoesProgramacao || '',
        anotacoesOperacionais: item.AnotacoesOperacionais || '',
        alerta: item.Alerta || '',
        statusColeta: toBooleanStatus(item.StatusColeta),
        statusEntrega: toBooleanStatus(item.StatusEntrega),
        statusDevolucao: toBooleanStatus(item.StatusDevolucao),
        statusBaixadoCheio: toBooleanStatus(item.StatusBaixadoCheio),
      }));

      setData(dadosProcessados);
      setTotalRegistros(responseData.totalRegistros || dadosProcessados.length);
      setResumoStatusPendentes(
        responseData.resumoStatus || responseData.resumoStatusPendentes || {},
      );
    } finally {
      if (requestId && isCurrentRequest(requestId)) {
        setLoading(false);
        setFilterLoading(false);
        inFlightSignatureRef.current = null;
      }
    }
  }, [beginRequest, filtros, isCurrentRequest, isMounted, paginationState.pageIndex, paginationState.pageSize]);

  useEffect(() => {
    if (!didInitialLoadRef.current) return;
    fetchData(filtros);
  }, [fetchData, filtros]);

  useEffect(() => {
    if (didInitialLoadRef.current) return;
    didInitialLoadRef.current = true;
    fetchData();
  }, [fetchData]);

  const handleFilterApply = useCallback((newFilters = {}) => {
    setFiltros((prev) => ({
      ...prev,
      ...newFilters,
    }));
    setFilterLoading(true);
    setPaginationState((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, []);

  const handleLimparFiltros = useCallback((clearedFilters) => {
    setFiltros(clearedFilters || initialFilters);
    setLoading(true);
    setFilterLoading(true);
    setPaginationState((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, []);

  const handlePaginationChange = useCallback((next) => {
    setLoading(true);
    setPaginationState((prev) => {
      if (typeof next === 'function') {
        return next(prev);
      }
      if (typeof next === 'number') {
        return {
          ...prev,
          pageIndex: next,
        };
      }
      if (next && typeof next === 'object') {
        return {
          ...prev,
          ...next,
        };
      }
      return prev;
    });
  }, []);

  const handleExcluir = useCallback(
    async (id) => {
      if (!id) return;
      const confirmar = window.confirm('Deseja excluir este depot?');
      if (!confirmar) return;
      try {
        await deleteDepots(id);
        notification.success('Depot excluido com sucesso!');
        fetchData();
      } catch (error) {
        console.error(error);
        notification.error('Erro ao excluir depot');
      }
    },
    [fetchData],
  );

  const handleToggleStatus = useCallback(
    async (id, statusKey, nextValueOverride) => {
      if (!id || !statusKey) return;

      const statusConfig = {
        coleta: { field: 'statusColeta', payloadKey: 'StatusColeta', label: 'coleta' },
        entrega: { field: 'statusEntrega', payloadKey: 'StatusEntrega', label: 'entrega' },
        devolucao: { field: 'statusDevolucao', payloadKey: 'StatusDevolucao', label: 'devolucao' },
        baixado: { field: 'statusBaixadoCheio', payloadKey: 'StatusBaixadoCheio', label: 'baixado' },
      };

      const config = statusConfig[statusKey];
      if (!config) return;

      const currentValue = !!data.find((row) => row.id === id)?.[config.field];
      const nextValue = typeof nextValueOverride === 'boolean' ? nextValueOverride : !currentValue;

      try {
        await updateDepotStatus(id, { [config.payloadKey]: nextValue });
        setData((prev) =>
          prev.map((row) =>
            row.id === id
              ? {
                  ...row,
                  [config.field]: nextValue,
                }
              : row,
          ),
        );
        notification.success(`Status de ${config.label} atualizado!`);
      } catch (error) {
        console.error(error);
        notification.error('Erro ao atualizar status do depot');
      }
    },
    [data],
  );

  const handleUpdateStatus = useCallback(async (id, nextStatus) => {
    if (!id || !nextStatus) return;

    try {
      await updateDepots(id, { Status: nextStatus });
      setData((prev) =>
        prev.map((row) =>
          row.id === id
            ? {
                ...row,
                status: nextStatus,
              }
            : row,
        ),
      );
      notification.success('Status do container atualizado!');
    } catch (error) {
      console.error(error);
      notification.error('Erro ao atualizar status do container');
      throw error;
    }
  }, []);

  return {
    data,
    loading,
    filterLoading,
    totalRegistros,
    resumoStatusPendentes,
    filtros,
    paginationState,
    handlePaginationChange,
    handleFilterApply,
    handleLimparFiltros,
    handleExcluir,
    handleToggleStatus,
    handleUpdateStatus,
  };
};
